import { buildApiUrl } from "./apiConfig";
import type { ListedContact } from "./extractListedContacts";
import { isValidListedContact } from "./extractListedContacts";

export type AddContactFromAssistResult =
    | "added"
    | "already"
    | "not_on_list"
    | "not_found"
    | "invalid"
    | "error";

interface AthleteCollegeRow {
    college?: { id?: number; name?: string };
    id?: number;
    name?: string;
}

interface SavedContact {
    fullName?: string;
    email?: string | null;
    title?: string | null;
}

function normalize(value: string | undefined | null): string {
    return (value ?? "").trim().toLowerCase();
}

function pickCollegeOnList(
    collegeName: string,
    rows: AthleteCollegeRow[],
): { id: number; name: string } | null {
    const wanted = normalize(collegeName);
    const mapped = rows
        .map((row) => ({
            id: row.college?.id ?? row.id,
            name: row.college?.name ?? row.name,
        }))
        .filter((row): row is { id: number; name: string } => row.id != null && Boolean(row.name));
    const exact = mapped.find((row) => normalize(row.name) === wanted);
    if (exact) {
        return exact;
    }
    return (
        mapped.find(
            (row) => normalize(row.name).includes(wanted) || wanted.includes(normalize(row.name)),
        ) ?? null
    );
}

function alreadySaved(contact: ListedContact, existing: SavedContact[]): boolean {
    return existing.some((row) => {
        if (contact.email && row.email && normalize(row.email) === normalize(contact.email)) {
            return true;
        }
        return (
            normalize(row.fullName) === normalize(contact.fullName) &&
            normalize(row.title) === normalize(contact.title)
        );
    });
}

export async function addContactFromAssist(
    username: string,
    contact: ListedContact,
): Promise<AddContactFromAssistResult> {
    if (!isValidListedContact(contact) || !username) {
        return "invalid";
    }
    try {
        const meRes = await fetch(buildApiUrl("/api/athletes/me"), {
            credentials: "include",
            headers: { Accept: "application/json" },
        });
        if (!meRes.ok) {
            return "error";
        }
        const me = (await meRes.json()) as { id?: number };
        if (me.id == null) {
            return "error";
        }

        const listRes = await fetch(
            buildApiUrl(`/api/colleges/athlete/${encodeURIComponent(username)}`),
            { credentials: "include", headers: { Accept: "application/json" } },
        );
        if (!listRes.ok) {
            return "error";
        }
        const rows = (await listRes.json()) as AthleteCollegeRow[];
        const college = pickCollegeOnList(contact.collegeName, rows);
        if (!college) {
            return "not_on_list";
        }

        const contactsRes = await fetch(
            buildApiUrl(`/api/athletes/${me.id}/colleges/${college.id}/contacts`),
            { credentials: "include", headers: { Accept: "application/json" } },
        );
        if (contactsRes.ok) {
            const existing = (await contactsRes.json()) as SavedContact[];
            if (alreadySaved(contact, existing)) {
                return "already";
            }
        }

        const addRes = await fetch(
            buildApiUrl(`/api/athletes/${me.id}/colleges/${college.id}/contacts`),
            {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    fullName: contact.fullName,
                    email: contact.email ?? null,
                    phone: contact.phone ?? null,
                    title: contact.title ?? null,
                }),
            },
        );
        if (addRes.status === 404) {
            return "not_found";
        }
        if (!addRes.ok) {
            return "error";
        }
        return "added";
    } catch {
        return "error";
    }
}
